/**
 * VCM Role Matrix — exact replica from demo.html
 *
 * Defines which actions (nav items / workspace cards) each role can access
 * within each module (Case Work / DCM).
 */

import type { VcmModule, ActionId } from '../../types/vcm';

export interface ActionCatalogEntry {
  label: string;
  description: string;
  icon: string;
  href: string;
}

/**
 * Action catalog — maps action IDs to their display properties.
 * Source: demo.html actionCatalog object (line 332-344)
 */
export const actionCatalog: Record<ActionId, ActionCatalogEntry> = {
  'new-client': {
    label: 'New Client Record',
    description: 'Search for an existing client before creating a new record.',
    icon: 'userPlus',
    href: '/clients/new',
  },
  'client-search': {
    label: 'Client Search',
    description: 'Search client records by case, client, or address.',
    icon: 'search',
    href: '/clients/search',
  },
  'manage-my-cases': {
    label: 'Manage My Cases',
    description: 'Open and edit cases assigned to the logged-in case manager.',
    icon: 'briefcase',
    href: '/cases/my',
  },
  'manage-org-cases': {
    label: 'Manage Organization Cases',
    description: 'Open and edit cases assigned to your organization.',
    icon: 'folder',
    href: '/cases/org',
  },
  'manage-export-org': {
    label: 'Manage & Export Organization Cases',
    description: 'Review and export cases assigned to your organization.',
    icon: 'export',
    href: '/cases/org-export',
  },
  'manage-all-cases': {
    label: 'Manage All Cases',
    description: 'Search, manage, and export all client records on the platform.',
    icon: 'users',
    href: '/cases/all',
  },
  'resource-library': {
    label: 'Resource Library',
    description: 'Search services and resources available to veterans.',
    icon: 'library',
    href: '/resources/library',
  },
  'manage-resources': {
    label: 'Manage Resources',
    description: 'Create new resource records and edit existing resources.',
    icon: 'settings',
    href: '/resources/manage',
  },
  'resource-export': {
    label: 'Resource Export',
    description: 'Export Resource Library records for review or reporting.',
    icon: 'export',
    href: '/resources/export',
  },
  dashboard: {
    label: 'Dashboard',
    description: 'Review program activity, tier distribution, and case workload.',
    icon: 'chart',
    href: '/events/dashboard',
  },
  'export-cases': {
    label: 'Export Cases',
    description: 'Export client and case records available to your role.',
    icon: 'export',
    href: '/export',
  },
  admin: {
    label: 'Admin',
    description: 'Verify intake, assign cases, manage programs, and review the audit log.',
    icon: 'admin',
    href: '/admin',
  },
  'call-center': {
    label: 'Call Center',
    description: 'Look up callers, log calls, make referrals, and register new clients by phone.',
    icon: 'phone',
    href: '/call-center',
  },
};

export interface ModuleConfig {
  label: string;
  roles: Record<string, ActionId[]>;
}

/**
 * Role matrix — exact replica from demo.html roleMatrix object (line 346-365)
 *
 * Each role within each module has a set of action IDs that determine:
 * 1. Which nav items they see
 * 2. Which workspace action cards appear on their dashboard
 */
export const roleMatrix: Record<VcmModule, ModuleConfig> = {
  casework: {
    label: 'Case Work',
    roles: {
      Caseworker: ['call-center', 'new-client', 'client-search', 'resource-library'],
      Supervisor: [
        'call-center',
        'new-client',
        'client-search',
        'resource-library',
        'manage-org-cases',
        'admin',
      ],
      'Organization Manager': [
        'call-center',
        'new-client',
        'client-search',
        'resource-library',
        'manage-org-cases',
        'dashboard',
        'export-cases',
        'admin',
      ],
      'Resource Management': ['call-center', 'manage-resources', 'resource-library', 'resource-export'],
    },
  },
  dcm: {
    label: 'Case Management',
    roles: {
      'Case Manager': ['call-center', 'new-client', 'client-search', 'manage-my-cases', 'resource-library'],
      Supervisor: [
        'call-center',
        'new-client',
        'client-search',
        'manage-my-cases',
        'resource-library',
        'manage-org-cases',
        'admin',
      ],
      'Organization Manager': [
        'call-center',
        'new-client',
        'client-search',
        'resource-library',
        'manage-my-cases',
        'manage-export-org',
        'dashboard',
        'export-cases',
        'admin',
      ],
      'Resource Management': ['call-center', 'manage-resources', 'resource-library', 'resource-export'],
      'Program Manager': [
        'call-center',
        'new-client',
        'client-search',
        'resource-library',
        'manage-all-cases',
        'dashboard',
        'export-cases',
        'admin',
      ],
    },
  },
};

export function getRolesForModule(module: VcmModule): string[] {
  return Object.keys(roleMatrix[module].roles);
}

export function getActionsForRole(module: VcmModule, role: string): ActionId[] {
  return roleMatrix[module].roles[role] ?? [];
}

export function getModuleLabel(module: VcmModule): string {
  return roleMatrix[module].label;
}
